import { type FunctionComponent, useCallback, useRef, useState } from 'react'

import { setTenant } from '~/actions/tenantActions'

import { useTenantsQuery } from '~/hooks/services/servicenow/queries/useTenantsQuery'

import { Popover, PopoverContent, PopoverTrigger } from '~/components/Common/PopoverDialog'

import { classNames } from '~/utils/classNames'

import { PiCaretDownBold } from 'react-icons/pi'
import { useDispatch, useSelector } from 'react-redux'
import { type RootStateOrAny } from 'react-redux'

import Button from './Button'

const TenantSelector: FunctionComponent = () => {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const dispatch = useDispatch()

  const tenant = useSelector((state: RootStateOrAny) => state.tenant)
  const { data: tenants = [], isLoading } = useTenantsQuery({ includeSubscriptions: false })

  const handleSelectTenant = useCallback(
    (selectedTenant: { id: string; name: string }) => {
      setOpen(false)
      dispatch(setTenant(selectedTenant))
      if (triggerRef.current) triggerRef.current.focus()
    },
    [dispatch]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>, tenantId: string) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        const selectedTenant = tenants.find((t) => t.id === tenantId)
        if (selectedTenant) {
          handleSelectTenant({ id: selectedTenant.id, name: selectedTenant.name })
        }
      }
    },
    [tenants, handleSelectTenant]
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button ref={triggerRef} aria-label="Select tenant" bordered>
          {isLoading ? 'Loading tenants...' : tenant?.name || 'Select tenant'}
          <PiCaretDownBold className="ml-2 h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 overflow-hidden !rounded-2xl !p-0" align="center">
        <div className="max-h-60 overflow-y-auto">
          {tenants.map((t) => (
            <button
              key={t.id}
              className={classNames(
                'w-full px-3 py-2 text-left text-sm focus:outline-none focus:ring-0',
                tenant?.id === t.id ? 'bg-th-content-secondary text-th-text' : 'text-th-text hover:bg-th-content-secondary'
              )}
              onClick={() => handleSelectTenant({ id: t.id, name: t.name })}
              onKeyDown={(e) => handleKeyDown(e, t.id)}
              role="menuitem"
              tabIndex={0}
              aria-selected={tenant?.id === t.id}
            >
              {t.name}
            </button>
          ))}
          {tenants.length === 0 && !isLoading && <div className="px-3 py-2 text-sm text-th-text-secondary">No tenants available</div>}
          {isLoading && (
            <div className="flex items-center justify-center px-3 py-2">
              <span className="text-sm text-th-text-secondary">Loading tenants...</span>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default TenantSelector
